import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

//Material UI
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import GetAppIcon from '@mui/icons-material/GetApp'
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

// Icons
import CancelIcon from '@mui/icons-material/Cancel';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssignmentIcon from '@mui/icons-material/Assignment';

//Components
import Filters from './InventoryFilters';
import Modal from '../../../components/modal';
import GridItems from '../../../components/grid/grid';
import ShowMovementsModal from './ShowMovementsModal';

//Hooks
import useStyles from './styles';
import useUser from '../../../hooks/user/useUser';
import useInventory from '../../../hooks/inventory/useInventory';
import useAuthorize from '../../../hooks/user/useAuthorize';
import useQueryString from '../../../hooks/queryString/useQueryString'

//Models
import { utils } from '../../../utils/utils';
import { Permissions } from '../../../models/Roles';
import { columns, title } from '../../../models/StockEntity';

// Service
import InventoryService from '../../../services/inventoryService';
import { FabContainer } from '@arredo/reactjs-ui';

const InventoryView = () => {
    //Hooks
    const { classes } = useStyles();
    const { branch } = useUser();
    const push = useNavigate();
    const isAuthorized = useAuthorize();
    const { setParameterSearch, clearFilters, filters } = useQueryString();
    const { stocks, categories, totalPage, totalResults, loading, setCategories } = useInventory(filters);

    //States
    const [selectedSKUs, setSelectedSKUs] = useState([]);
    const [exportSelected, setExportSelected] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [openLoadingModal, setOpenLoadingModal] = useState(false);

    //Methods
    const mapRequest = () => {
        return {
            ...filters,
            isExport: true,
            warehouseIds: [branch.warehouseId],
            skus: filters.skus
                ? filters.skus.toString().split(',').map(s => s.trim())
                : null,
            categoryIds: categories?.length
                ? categories
                : null,
            moreOrEqualTo: filters?.moreOrEqualTo
                ? Number(filters?.moreOrEqualTo)
                : null,
            lessOrEqualTo: filters?.lessOrEqualTo
                ? Number(filters?.lessOrEqualTo)
                : null
        }
    }

    //Handlers
    const handleChangePage = (e, page) => setParameterSearch({ pageNumber: Number(page) })
    const handleFilterChange = (parameters) => setParameterSearch({ ...parameters, pageNumber: 1 });
    const showProductHistory = (item) => push(`/stocks/product-history?refId=${item.refId}&from=${utils.getDateNow()}&to=${utils.getDateNow()}`);

    const handleCategoryChange = (categories) => {
        setCategories(categories);
        setParameterSearch({ pageNumber: 1 })
    }

    const handleClearSelected = () => setSelectedSKUs([]);
    const handleModalClose = () => setOpenLoadingModal(false);
    const handleSelect = (checked, item) =>
        setSelectedSKUs(checked
            ? [...new Set([...selectedSKUs, item.refId])]
            : selectedSKUs.filter(refId => refId !== item.refId));

    const handleClickExport = async () => {
        setOpenLoadingModal(true);

        const blob = selectedSKUs && selectedSKUs.length > 0
            ? await InventoryService.ExportRequest({ skusByWarehouse: selectedSKUs.map(sku => ({ sku, warehouseId: branch.warehouseId })) })
            : await InventoryService.ExportRequest(mapRequest());

        utils.export(blob, 'Stocks');
        setOpenLoadingModal(false);
    }

    return (
        <>
            {
                selectedProduct &&
                <ShowMovementsModal
                    open={Boolean(selectedProduct)}
                    onClose={() => setSelectedProduct(null)}
                    product={selectedProduct}
                />
            }
            {
                openLoadingModal &&
                <Modal
                    fullWidth
                    disableEscapeKeyDown
                    open={openLoadingModal}
                    onClose={handleModalClose}
                >
                    {
                        openLoadingModal
                            ? <Grid>
                                <Grid container justifyContent='center'>
                                    <CircularProgress />
                                </Grid>
                                <Grid container justifyContent='center'>
                                    <Typography variant='h5'>
                                        Generando el archivo de Excel
                                    </Typography>
                                </Grid>
                            </Grid>
                            : null
                    }
                </Modal>
            }
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Paper className={classes.paper}>
                        <Typography variant="h4" children={title} />
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Filters
                        filters={filters}
                        setFilters={handleFilterChange}
                        setFilterCategories={handleCategoryChange}
                        clearFilters={clearFilters}
                    />
                </Grid>

                <Grid size={12}>
                    <GridItems
                        columns={columns}
                        rows={stocks}
                        loading={loading}
                        paginationProps={{
                            count: totalPage,
                            page: filters?.pageNumber || 1,
                            onChange: handleChangePage
                        }}
                        rowPros={{
                            onSelect: isAuthorized([Permissions.StockExport]) && handleSelect,
                            isSelected: (item) => selectedSKUs.some(refId => refId === item.refId),
                            customCells: isAuthorized([Permissions.StockMovementsHistoryView]) &&
                                (
                                    (item) =>
                                        <Tooltip title='Movimientos' aria-label='Movimientos'>
                                            <IconButton onClick={() => showProductHistory(item)} color="primary" size="large">
                                                <TimelineIcon />
                                            </IconButton>
                                        </Tooltip>
                                )
                        }}
                    />
                </Grid>
            </Grid>
            <FabContainer>
                {
                    !exportSelected &&
                    isAuthorized([Permissions.StockExport]) &&
                    <Fab
                        color='primary'
                        variant="extended"
                        className={classes.fab}
                        onClick={() => setExportSelected(true)}
                    >
                        <GetAppIcon className={classes.extendedIcon} />
                        Exportar
                    </Fab>
                }
                {
                    !exportSelected &&
                    isAuthorized([Permissions.StockControl]) &&
                    <Fab
                        color='primary'
                        variant="extended"
                        className={classes.fab}
                        onClick={() => push('/stockControls/create')}
                    >
                        <AssignmentIcon className={classes.extendedIcon} />
                        Control
                    </Fab>
                }
                {
                    exportSelected &&
                    isAuthorized([Permissions.StockExport]) &&
                    <>
                        <Fab
                            color='primary'
                            variant='extended'
                            onClick={handleClearSelected}
                        >
                            Deseleccionar todos
                        </Fab>

                        <Fab
                            color='primary'
                            variant="extended"
                            className={classes.fab}
                            onClick={handleClickExport}
                        >
                            <GetAppIcon className={classes.extendedIcon} />
                            {`Exportar ${selectedSKUs?.length ? `seleccionados (${selectedSKUs?.length})` : `todos (${totalResults || 0})`} `}
                        </Fab>

                        <Fab
                            color='secondary'
                            variant="extended"
                            className={classes.fab}
                            onClick={() => setExportSelected(false)}
                        >
                            <CancelIcon className={classes.extendedIcon} />
                            Cancelar
                        </Fab>
                    </>
                }
            </FabContainer>
        </>
    );
}

export default InventoryView;